import { merge, of, Subject } from 'rxjs';
import {
	filter, mergeMap, switchMap, takeUntil,
} from 'rxjs/operators';

import { Container, Service } from '../../../../symphony';

import { SlActions } from './sl-actions';
import { SlCodeBitsContext } from '../../global-constants';
import { SlActionsController } from './sl-actions.controller';
import { IAction, ActionType, RequestType } from '../../global-interface';

@Service()
export class SlActionsService {
	private actions$ = Container.take(SlCodeBitsContext, SlActions).actions$;

	private controller = Container.take(SlCodeBitsContext, SlActionsController);

	private cancelableActionsMap = {};

	private parallelActionsMap = {
		// Mount events
		[ActionType.OnHotTodayMount]: this.controller.onHotTodayMountHandler,
		[ActionType.OnCodeBitsListMount]:
            this.controller.onCodeBitsListMountHandler,
		[ActionType.OnCodeBitsListPageMountHandler]:
            this.controller.onCodeBitsListPageMountHandler,

		// Click events
		[ActionType.OnProfileButtonClick]:
            this.controller.onProfileButtonClickHandler,
		[ActionType.OnCodeBitClick]: this.controller.onProjectClickHandler,
		[ActionType.OnTopicClick]: this.controller.onTopicClickHandler,
		[ActionType.OnSearchClick]: this.controller.onSearchClickHandler,
		[ActionType.OnNewCodeClick]: this.controller.onNewCodeClickHandler,
		[ActionType.OnPaginationClick]:
            this.controller.onPaginationClickHandler,
		[ActionType.OnEmptyCodeBitsCTAClick]:
            this.controller.onEmptyCodeBitsCTAClickHandler,

		// Business events
		[ActionType.GetCodeBitsList]: this.controller.getCodeBitsListHandler,
		[ActionType.GetLanguagesCatalog]:
            this.controller.getLanguagesCatalogHandler,
		[ActionType.GetHotTodayTopics]:
            this.controller.getHotTodayTopicsHandler,
		[ActionType.OrderFilterChange]:
            this.controller.orderFilterChangeHandler,
		[ActionType.LanguageFilterChange]:
            this.controller.languageFilterChangeHandler,

		// Track Actions
		[ActionType.Track]: this.controller.onTrackActionHandler,
	};

	constructor() {
		const unsubscriber: Subject<void> = Container.take(
			SlCodeBitsContext,
			'unsubscriber',
		);
		const cancelable = this.actions$.pipe(
			filter((a) => !!a && a.requestType === RequestType.Cancelable),
			switchMap((action: IAction) => {
				const handler = this.cancelableActionsMap[action.type];
				return handler ? handler(action.data) : of(action);
			}),
		);

		const parallel = this.actions$.pipe(
			filter((a) => !!a && a.requestType === RequestType.Parallel),
			mergeMap((action: IAction) => {
				const handler = this.parallelActionsMap[action.type];
				return handler ? handler(action.data) : of(action);
			}),
		);

		merge(cancelable, parallel)
			.pipe(
				filter((a) => !!a),
				takeUntil(unsubscriber),
			)
			.subscribe();
	}
}
